import { createApi } from "@reduxjs/toolkit/query/react";
import { customBaseQuery } from "../../common/reduxQuery/helpers";
import {
  ConfigMapListItem,
  ConfigMapDetail,
  ConfigMapCreateRequest,
  ConfigMapUpdateRequest,
  ConfigMapBulkSaveRequest,
  ConfigMapExportPayload,
  EntityTypeUsageResponseDto,
  DocumentTypeDto,
} from "../interfaces/configMap";

export interface DocumentTypeCreateRequest {
  name: string;
  shortDescription?: string;
  longDescription?: string;
  docaiProcessorId?: string;
  docaiProcessorVersionId?: string;
  linkingPipelineId?: string;
  configMapId: string;
  enforceRequiredCategorizations?: boolean;
  enforceRequiredEntities?: boolean;
}

export const configMapEditorApi = createApi({
  reducerPath: "configMapEditorApi",
  baseQuery: customBaseQuery(),
  tagTypes: ["ConfigMapList", "ConfigMapDetail"],
  endpoints: (build) => ({
    // ========== Config Map ==========
    getConfigMaps: build.query<
      ConfigMapListItem[],
      { inputIds?: string[] } | void
    >({
      query: (arg) => {
        const inputIds = arg?.inputIds;
        const qs =
          inputIds && inputIds.length > 0
            ? `?inputIds=${inputIds.join(",")}`
            : "";
        return { url: `/api/v1/configmap${qs}`, method: "GET" };
      },
      providesTags: ["ConfigMapList"],
    }),

    getConfigMapDetail: build.query<ConfigMapDetail, string>({
      query: (id) => ({ url: `/api/v1/configmap/${id}`, method: "GET" }),
      providesTags: (_result, _error, id) => [{ type: "ConfigMapDetail", id }],
    }),

    createConfigMap: build.mutation<ConfigMapDetail, ConfigMapCreateRequest>({
      query: (data) => ({
        url: "/api/v1/configmap",
        body: data,
        method: "POST",
      }),
      invalidatesTags: ["ConfigMapList"],
    }),

    updateConfigMap: build.mutation<
      ConfigMapDetail,
      { id: string; data: ConfigMapUpdateRequest }
    >({
      query: ({ id, data }) => ({
        url: `/api/v1/configmap/${id}`,
        body: data,
        method: "PUT",
      }),
      invalidatesTags: (_result, _error, { id }) => [
        "ConfigMapList",
        { type: "ConfigMapDetail", id },
      ],
    }),

    bulkSaveConfigMap: build.mutation<
      ConfigMapDetail,
      { id: string; data: ConfigMapBulkSaveRequest }
    >({
      query: ({ id, data }) => ({
        url: `/api/v1/configmap/${id}/bulk`,
        body: data,
        method: "PUT",
      }),
      // Note: ConfigMapDetail is intentionally NOT invalidated here. The save
      // handler in useConfigMapDraft does an explicit refetch after all parallel
      // PUTs (categorizations / normalizations / entity types) resolve, then
      // re-initializes the draft from that result. Letting RTK auto-refetch on
      // its own raced with the explicit refetch and produced flicker.
      invalidatesTags: ["ConfigMapList"],
    }),

    getEntityTypeUsage: build.query<
      EntityTypeUsageResponseDto[],
      { configMapId: string; entityTypeIds: string[] }
    >({
      query: ({ configMapId, entityTypeIds }) => ({
        url: `/api/v1/configmap/${configMapId}/entity-type-usage`,
        body: { entityTypeIds },
        method: "POST",
      }),
    }),

    deleteConfigMap: build.mutation<void, string>({
      query: (id) => ({
        url: `/api/v1/configmap/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["ConfigMapList"],
    }),

    cloneConfigMap: build.mutation<
      ConfigMapDetail,
      { id: string; displayName?: string }
    >({
      query: ({ id, displayName }) => ({
        url: `/api/v1/configmap/${id}/clone${
          displayName ? `?displayName=${encodeURIComponent(displayName)}` : ""
        }`,
        method: "POST",
      }),
      invalidatesTags: (result) =>
        result
          ? ["ConfigMapList", { type: "ConfigMapDetail", id: result.id }]
          : ["ConfigMapList"],
    }),

    exportConfigMap: build.query<ConfigMapExportPayload, string>({
      query: (id) => ({ url: `/api/v1/configmap/${id}/export`, method: "GET" }),
    }),

    importConfigMap: build.mutation<
      ConfigMapDetail,
      { payload: ConfigMapExportPayload; onConflict?: "fail" | "overwrite" }
    >({
      query: ({ payload, onConflict }) => ({
        url: `/api/v1/configmap/import${
          onConflict ? `?onConflict=${onConflict}` : ""
        }`,
        body: payload,
        method: "POST",
      }),
      invalidatesTags: (result) =>
        result
          ? ["ConfigMapList", { type: "ConfigMapDetail", id: result.id }]
          : ["ConfigMapList"],
    }),

    // ========== Document Types ==========
    createDocumentType: build.mutation<
      DocumentTypeDto,
      DocumentTypeCreateRequest
    >({
      query: (data) => ({
        url: "/api/v1/document-type",
        body: data,
        method: "POST",
      }),
      invalidatesTags: ["ConfigMapList"],
    }),

    deleteDocumentType: build.mutation<void, string>({
      query: (id) => ({ url: `/api/v1/document-type/${id}`, method: "DELETE" }),
      invalidatesTags: ["ConfigMapList"],
    }),
  }),
});

export const {
  useGetConfigMapsQuery,
  useLazyGetConfigMapsQuery,
  useGetConfigMapDetailQuery,
  useCreateConfigMapMutation,
  useUpdateConfigMapMutation,
  useBulkSaveConfigMapMutation,
  useGetEntityTypeUsageQuery,
  useLazyGetEntityTypeUsageQuery,
  useDeleteConfigMapMutation,
  useCloneConfigMapMutation,
  useLazyExportConfigMapQuery,
  useImportConfigMapMutation,
  useCreateDocumentTypeMutation,
  useDeleteDocumentTypeMutation,
} = configMapEditorApi;
